import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * Carta de ventas: RESULTADO → FACILIDAD → QUÉ APRENDÉS → VARIEDAD →
 * DESEO (rellenos) → OPORTUNIDAD → PRUEBA SOCIAL (maqueta) → BONOS →
 * OFERTA + PRECIO → PARA QUIÉN ES → KIT → SEGUNDO CIERRE → GARANTÍA →
 * FAQ → FOOTER.
 *
 * Regla de conversión: ningún CTA anterior al primer bloque de precio abre
 * el checkout; todos anclan a #oferta.
 *
 * Todo lo que está debajo del primer pantallazo se carga en chunks aparte para
 * que el Hero (LCP en mobile) llegue con el menor JS posible. Cada bloque
 * reserva altura mientras carga, así el CLS queda en cero.
 */
const DesireSection = lazy(() => import('./components/DesireSection').then(m => ({
  default: m.DesireSection
})));
const EasySection = lazy(() => import('./components/EasySection').then(m => ({
  default: m.EasySection
})));
const AudienceSection = lazy(() => import('./components/AudienceSection').then(m => ({
  default: m.AudienceSection
})));
const CatalogSection = lazy(() => import('./components/CatalogSection').then(m => ({
  default: m.CatalogSection
})));
const FlavorsSection = lazy(() => import('./components/FlavorsSection').then(m => ({
  default: m.FlavorsSection
})));
const StuffedSection = lazy(() => import('./components/StuffedSection').then(m => ({
  default: m.StuffedSection
})));
const SellSection = lazy(() => import('./components/SellSection').then(m => ({
  default: m.SellSection
})));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection').then(m => ({
  default: m.TestimonialsSection
})));
const BonosSection = lazy(() => import('./components/BonosSection').then(m => ({
  default: m.BonosSection
})));
const TomosSection = lazy(() => import('./components/TomosSection').then(m => ({
  default: m.TomosSection
})));
const OfferSection = lazy(() => import('./components/OfferSection').then(m => ({
  default: m.OfferSection
})));
const GuaranteeSection = lazy(() => import('./components/GuaranteeSection').then(m => ({
  default: m.GuaranteeSection
})));
const FaqSection = lazy(() => import('./components/FaqSection').then(m => ({
  default: m.FaqSection
})));
const FinalCta = lazy(() => import('./components/FinalCta').then(m => ({
  default: m.FinalCta
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const StickyBuyBar = lazy(() => import('./components/StickyBuyBar').then(m => ({
  default: m.StickyBuyBar
})));
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
}
function Deferred({
  children,
  minHeight,
  background = 'bg-ivory'
}: DeferredProps) {
  return <Suspense fallback={<div className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="3ljphoo1kr3ur" />} data-id="element-0" data-mp="3lk7mwa67sset">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full bg-ivory text-charcoal" data-id="element-2" data-mp="2svtqtzyos65w">
      {/* RESULTADO */}
      <Hero data-id="element-3" data-mp="2stplrep1x141" />

      <Deferred minHeight="500px" background="bg-ivory" data-id="element-4" data-mp="18driltpr7bcr">
        <DesireSection data-id="element-5" data-mp="18c6ou6b9jwfy" />
      </Deferred>

      {/* FACILIDAD + QUÉ VAS A APRENDER (fusionadas) */}
      <Deferred minHeight="1100px" background="bg-cream" data-id="element-6" data-mp="1d62iwxoeoz5u">
        <EasySection data-id="element-7" data-mp="yg3n31kbpztj" />
      </Deferred>

      {/* VARIEDAD: el catálogo de 100 */}
      <Deferred minHeight="860px" background="bg-bone" data-id="element-8" data-mp="13eoecbhll95u">
        <CatalogSection data-id="element-9" data-mp="13f7tjkfv5jg6" />
      </Deferred>

      {/* GALERÍA */}
      <Deferred minHeight="1600px" data-id="element-10" data-mp="1r3zh1yiiddmm">
        <FlavorsSection data-id="element-11" data-mp="1r4sj4m6iveyf" />
      </Deferred>

      {/* DESEO: el relleno */}
      <Deferred minHeight="900px" background="bg-ivory" data-id="element-12" data-mp="1w2asgdogbw6y">
        <StuffedSection data-id="element-13" data-mp="1i3ksmqa8ziew" />
      </Deferred>

      {/* OPORTUNIDAD: de la cocina al catálogo */}
      <Deferred minHeight="900px" background="bg-ivory" data-id="element-14" data-mp="1m562e2rhjgkv">
        <SellSection data-id="element-15" data-mp="1m3v2ie15usz1" />
      </Deferred>

      {/* PRUEBA SOCIAL — maqueta con testimonios placeholder */}
      <Deferred minHeight="820px" background="bg-cream" data-id="element-16" data-mp="6mukv2q6eij6">
        <TestimonialsSection data-id="element-17" data-mp="aqis1kawbc3w" />
      </Deferred>

      {/* LOS 5 BONOS — carrusel, antes de la primera aparición del precio */}
      <Deferred minHeight="560px" background="bg-bone" data-id="element-18" data-mp="qxxe9fvnoy3x">
        <BonosSection data-id="element-19" data-mp="m6p2wearn2o4" />
      </Deferred>

      {/* PRIMERA APARICIÓN DEL PRECIO: bloque principal de oferta (#oferta) */}
      <Deferred minHeight="780px" background="bg-[#E9F0D5]" data-id="element-20" data-mp="h6kmeo9uy7xw">
        <OfferSection data-id="element-21" data-mp="h4znynkfa4ur" />
      </Deferred>

      {/* PARA QUIÉN ES */}
      <Deferred minHeight="820px" background="bg-ivory" data-id="element-22" data-mp="c9u7435vngdj">
        <AudienceSection data-id="element-23" data-mp="19wje6rffsj69" />
      </Deferred>

      {/* EL KIT COMPLETO — versión compacta, sin repetir explicaciones */}
      <Deferred minHeight="620px" background="bg-ivory" data-id="element-24" data-mp="14xyequ80e3iv">
        <TomosSection data-id="element-25" data-mp="zxtuct3zcond" />
      </Deferred>

      {/* SEGUNDO CIERRE COMERCIAL */}
      <Deferred minHeight="900px" data-id="element-26" data-mp="zzwu44uxpw5a">
        <FinalCta data-id="element-27" data-mp="vwrv4ca2ccle" />
      </Deferred>

      {/* GARANTÍA */}
      <Deferred minHeight="360px" background="bg-cream" data-id="element-28" data-mp="vyb9fxc5sdkj">
        <GuaranteeSection data-id="element-29" data-mp="3kp426rbcnlbt" />
      </Deferred>

      {/* OBJECIONES */}
      <Deferred minHeight="820px" background="bg-ivory" data-id="element-30" data-mp="3gmpiy63rqjk0">
        <FaqSection data-id="element-31" data-mp="3gl65en9hymlx" />
      </Deferred>

      <Deferred minHeight="340px" background="bg-bone" data-id="element-32" data-mp="1w4ovmhq01uvx">
        <Footer data-id="element-33" data-mp="1w69ps68ejbt9" />
      </Deferred>

      <Suspense fallback={null} data-id="element-34" data-mp="2060du26jndu3">
        <StickyBuyBar data-id="element-35" data-mp="2071zf0l6riq2" />
      </Suspense>
    </main>;
}